<template>
    <div class="orders-tracking">
        <v-card class="section-card" elevation="0">
            <div class="card-head">
                <div class="head-gradient head-gradient--green" />
            </div>

            <v-divider />

            <!-- Status filter tabs -->
            <div class="status-tabs">
                <button v-for="tab in tabs" :key="tab.value"
                    :class="['tab-btn', { 'tab-btn--active': activeTab === tab.value }]" @click="setTab(tab.value)">
                    {{ tab.label }}
                    <span class="tab-count">{{ countByTab(tab.value) }}</span>
                </button>
            </div>

            <v-divider class="mb-5" />

            <div class="section-form pt-0">
                <!-- Loading -->
                <div v-if="loading" class="py-8 d-flex justify-center">
                    <v-progress-circular indeterminate size="32" width="3" color="#16a34a" />
                </div>

                <!-- Empty -->
                <div v-else-if="filteredItems.length === 0" class="empty-wrap">
                    <div class="empty-card">
                        <v-icon size="40" color="#9ca3af" class="mb-2">mdi-cart-off</v-icon>
                        <div class="empty-title">ไม่มีคำสั่งซื้อในสถานะนี้</div>
                        <div class="empty-sub">สั่งซื้อสินค้าที่น่าสนใจได้ใน Marketplace</div>
                    </div>
                </div>

                <!-- Orders -->
                <div v-else class="orders-list">
                    <div v-for="(order, index) in pagedItems" :key="order.id" class="order-block">
                        <OrderCard :order="order" :is-tracking="true" @refresh="fetchData" />
                        <v-divider v-if="index < pagedItems.length - 1" class="my-6" />
                    </div>

                    <div class="pagination-row mt-6" v-if="filteredItems.length > itemsPerPage">
                        <v-pagination v-model="page" :length="pageCount" :total-visible="7" prev-icon="<" next-icon=">"
                            class="mx-2" color="#d6230b" />
                    </div>
                </div>
            </div>
        </v-card>
    </div>
</template>

<script>
import { getOrderHistoryApi } from "@/services/api/userServices";
import OrderCard from "./OrderCard.vue";

const STATUS_MAP = {
    all: null,
    pending_payment: ["pending", "request_paid"],
    to_ship: ["paid", "packed"],
    to_receive: ["shipped", "out_for_delivery"],
    completed: ["delivered"],
    cancelled: ["cancelled", "refunded"],
};

export default {
    name: "OrderTracking",
    components: {
        OrderCard
    },
    data() {
        return {
            loading: false,
            items: [],
            page: 1,
            itemsPerPage: 5,
            activeTab: "all",
            tabs: [
                { label: "ทั้งหมด", value: "all" },
                { label: "ที่ต้องชำระ", value: "pending_payment" },
                { label: "ที่ต้องจัดส่ง", value: "to_ship" },
                { label: "ที่ต้องได้รับ", value: "to_receive" },
                { label: "สำเร็จแล้ว", value: "completed" },
                { label: "ยกเลิก", value: "cancelled" },
            ],
        };
    },

    computed: {
        filteredItems() {
            const statuses = STATUS_MAP[this.activeTab];
            if (!statuses) return this.items;
            return this.items.filter(o => statuses.includes(o.currentStatus));
        },
        pageCount() {
            return Math.max(1, Math.ceil(this.filteredItems.length / this.itemsPerPage));
        },
        pagedItems() {
            const start = (this.page - 1) * this.itemsPerPage;
            return this.filteredItems.slice(start, start + this.itemsPerPage);
        },
    },

    methods: {
        async fetchData() {
            this.loading = true;
            try {
                const res = await getOrderHistoryApi();
                this.items = Array.isArray(res?.data) ? res.data : [];
            } catch (e) {
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        setTab(value) {
            this.activeTab = value;
            this.page = 1;
        },
        countByTab(value) {
            const statuses = STATUS_MAP[value];
            if (!statuses) return this.items.length;
            return this.items.filter((o) => statuses.includes(o.currentStatus)).length;
        },
        canReview(order) {
            return order.currentStatus === "delivered";
        },
        formatPrice(price) {
            return parseFloat(price || 0).toLocaleString("en-US", {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
            });
        },
        getStatusLabel(status) {
            const dict = {
                pending: "รอชำระเงิน",
                request_paid: "แจ้งชำระเงินแล้ว",
                paid: "ชำระเงินแล้ว",
                packed: "กำลังแพ็คสินค้า",
                shipped: "จัดส่งแล้ว",
                out_for_delivery: "กำลังนำจ่าย",
                delivered: "พัสดุจัดส่งสำเร็จ",
                cancelled: "ยกเลิกแล้ว",
                refunded: "คืนเงินแล้ว",
            };
            return dict[status] || status;
        },
        getStatusColor(status) {
            const dict = {
                pending: "#F4811F",
                delivered: "#d6230b",
                paid: "#d6230b",
                cancelled: "#9e9e9e",
            };
            return dict[status] || "#d6230b";
        },
    },

    mounted() {
        this.fetchData();
    },
};
</script>

<style scoped>
.section-card {
    background: #fff;
    border: 1px solid #f1f5f9;
    border-radius: 16px;
    box-shadow: 0 12px 28px rgba(2, 6, 23, 0.06);
}

.card-head {
    border-top-left-radius: 16px;
    border-top-right-radius: 16px;
    overflow: hidden;
}

.head-gradient {
    height: 76px;
    opacity: 0.18;
}

.head-gradient--green {
    background: linear-gradient(100deg, #d6230b 0%, #eed7d5 100%);
}


.head-inner {
    padding: 22px 24px 16px;
}

.form-title {
    font-size: 22px;
    font-weight: 800;
    color: #0f172a;
    margin-bottom: 6px;
    font-family: "Noto Sans Thai", system-ui, Arial, sans-serif;
}

/* ─── Status tabs ─────────────────────── */
.status-tabs {
    display: flex;
    align-items: stretch;
    overflow-x: auto;
}

.tab-btn {
    flex: 1;
    min-width: 100px;
    padding: 14px 8px;
    font-size: 14px;
    font-weight: 500;
    background: none;
    border: none;
    border-bottom: 3px solid transparent;
    color: #616161;
    cursor: pointer;
    white-space: nowrap;
    transition: color 0.2s, border-color 0.2s;
}

.tab-btn:hover {
    color: #d6230b;
}

.tab-btn--active {
    color: #d6230b;
    border-bottom-color: #d6230b;
    font-weight: 700;
}

.tab-count {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 20px;
    padding: 0 7px;
    margin-left: 6px;
    border-radius: 999px;
    background: #f1f5f9;
    color: #475569;
    font-size: 12px;
    font-weight: 700;
    line-height: 1;
}
.tab-btn--active .tab-count {
    background: #d6230b;
    color: #fff;
}

/* ─── Body ────────────────────────────── */
.section-form {
    padding: 22px 24px 28px;
}

.empty-wrap {
    text-align: center;
    padding: 60px 0;
}

.empty-title {
    font-size: 18px;
    font-weight: 700;
    color: #212121;
}

.empty-sub {
    color: #757575;
}

.pagination-row {
    display: flex;
    justify-content: center;
}

::v-deep .v-pagination__item {
    box-shadow: none !important;
    border: 1px solid #e0e0e0;
}

::v-deep .v-pagination__navigation {
    box-shadow: none !important;
    border: 1px solid #e0e0e0;
}

@media (max-width: 600px) {
    .section-form {
        padding: 12px 14px 20px;
    }

    .tab-btn {
        min-width: 80px;
        padding: 10px 6px;
        font-size: 13px;
    }
}
</style>
